import { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SelectedEmployeeProvider } from './context/SelectedEmployeeContext';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import Header from './components/Header';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const ErrorPage = lazy(() => import('./pages/ErrorPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const LeavePage = lazy(() => import('./pages/LeavePage'));
const SalaryPage = lazy(() => import('./pages/SalaryPage'));
const SalaryProcessingPage = lazy(() => import('./pages/SalaryProcessingPage'));
const ChangeLogPage = lazy(() => import('./pages/ChangeLogPage'));
const BonusPage = lazy(() => import('./pages/BonusPage'));
const AdminHomePage = lazy(() => import('./pages/AdminHomePage'));

function LoadingFallback() {
  return (
    <div className="loader" style={{ minHeight: '100vh' }}>
      <div className="spinner"></div>
    </div>
  );
}

function ErrorRoute({ type }) {
  const { isLoggedIn } = useAuth();

  if (!isLoggedIn) {
    return <ErrorPage type={type} />;
  }

  return (
    <div className="app-container">
      <Header />
      <main className="main-content">
        <ErrorPage type={type} embedded />
      </main>
    </div>
  );
}

function RootRedirect() {
  const { isLoggedIn, isSuperAdmin } = useAuth();
  return <Navigate to={isLoggedIn ? (isSuperAdmin ? '/home' : '/profile') : '/login'} replace />;
}

function SuperAdminRoute({ children }) {
  const { isLoggedIn, isSuperAdmin } = useAuth();
  if (!isLoggedIn) {
    return <Navigate to="/error/401" replace />;
  }
  if (!isSuperAdmin) {
    return <Navigate to="/error/403" replace />;
  }
  return (
    <ProtectedRoute>
      {children}
    </ProtectedRoute>
  );
}

function AppRoutes() {
  const location = useLocation();
  const maintenanceEnabled = String(process.env.REACT_APP_MAINTENANCE_MODE || '').toLowerCase() === 'true';

  if (maintenanceEnabled && location.pathname !== '/maintenance') {
    return <ErrorRoute type="503" />;
  }

  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/error/401" element={<ErrorRoute type="401" />} />
      <Route path="/error/403" element={<ErrorRoute type="403" />} />
      <Route path="/error/500" element={<ErrorRoute type="500" />} />
      <Route path="/maintenance" element={<ErrorRoute type="503" />} />
      <Route path="/network-error" element={<ErrorRoute type="network" />} />
      <Route
        path="/home"
        element={
          <SuperAdminRoute>
            <div className="app-container">
              <AdminHomePage />
            </div>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/salary-processing"
        element={
          <SuperAdminRoute>
            <div className="app-container">
              <SalaryProcessingPage />
            </div>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/change-log"
        element={
          <SuperAdminRoute>
            <div className="app-container">
              <ChangeLogPage />
            </div>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <div className="app-container">
              <ProfilePage />
            </div>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leaves"
        element={
          <ProtectedRoute>
            <div className="app-container">
              <LeavePage />
            </div>
          </ProtectedRoute>
        }
      />
      <Route
        path="/salary"
        element={
          <ProtectedRoute>
            <div className="app-container">
              <SalaryPage />
            </div>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bonus"
        element={
          <ProtectedRoute>
            <div className="app-container">
              <BonusPage />
            </div>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<ErrorRoute type="404" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <SelectedEmployeeProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <ErrorBoundary>
            <Suspense fallback={<LoadingFallback />}>
              <AppRoutes />
            </Suspense>
          </ErrorBoundary>
        </Router>
      </SelectedEmployeeProvider>
    </AuthProvider>
  );
}
